import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Zap } from 'lucide-react'
import { CountUp, FadeIn, MaskRevealInView, ParallaxDrift, SettleIn, StaggerContainer, StaggerItem } from '@/components/motion'
import { ProductCard } from '@/components/ProductCard'
import { homeProofStats, products } from '@/data/products'
import { PATHS } from '@/app/router/routes'
import { setJsonLd, setPageMeta } from '@/utils/seo'
import { isPrerender } from '@/utils/prerender'
import { SampleCall } from '@/components/SampleCall'

function daypartLine(): string {
  if (isPrerender()) return 'What you can use right now'
  const now = new Date()
  const time = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const h = now.getHours()
  if (h >= 22 || h < 5) return `It's ${time} where you are. An AI receptionist doesn't sleep.`
  if (h < 12) return `It's ${time} where you are. An AI can pick up whenever the phone rings.`
  if (h < 17) return `It's ${time} where you are. A receptionist that never takes lunch.`
  return `It's ${time} where you are. The office closes. The line doesn't have to.`
}

export default function Home() {
  const [typeStarted, setTypeStarted] = useState(false)
  const [statusLine] = useState(daypartLine)

  useEffect(() => {
    const start = () => setTypeStarted(true)
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(start, { timeout: 2500 })
      return () => window.cancelIdleCallback(id)
    }
    const t = setTimeout(start, 1500)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    setPageMeta({
      title: 'Returned.com — AI Products That Deepen Human Connection',
      description: 'AI products that deepen human connection. Meet Reeva, SayWordy, Loveline, and Hold For Me.',
      canonical: 'https://returned.com/',
      robots: 'index, follow',
    })
    setJsonLd('home-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Returned.com',
      url: 'https://returned.com/',
      logo: 'https://returned.com/favicon-192x192.png',
      description: 'AI products that deepen human connection. Reeva, SayWordy, Loveline, and Hold For Me.',
      brand: products.map(({ name, url }) => ({ '@type': 'Brand', name, url })),
      sameAs: [
        'https://twitter.com/returnedcom',
        'https://www.linkedin.com/company/returnedcom/',
        'https://www.instagram.com/returnedcom/',
        'https://www.tiktok.com/@returnedcom',
      ],
    })
    return () => setJsonLd('home-jsonld', null)
  }, [])

  return (
    <>
      <section className="landing-section home-hero home-hero--kinetic">
        <div className="landing-container home-hero-grid">
          <div className="home-hero-copy">
            <FadeIn useScroll={false}>
              <p className="home-kicker">Returned.com</p>
              <h1 className="home-hero-title">Life is too short for friction.</h1>
              <p className="home-hero-brand-line">AI products that deepen human connection.</p>
              <p className="home-hero-subtitle">
                AI for the moments that happen on the phone: Reeva answers your business, SayWordy helps you talk across languages, Hold For Me waits in the queue, and Loveline keeps a loved one’s voice close.
              </p>
            </FadeIn>

            <FadeIn delay={0.1} useScroll={false}>
              <div className="home-hero-actions">
                <a href="https://helloreeva.com" target="_blank" rel="noopener noreferrer" className="home-hero-cta home-hero-cta--primary">
                  Join the Reeva beta <ArrowRight size={16} />
                </a>
                <a href="#products-launchpad" className="home-hero-cta home-hero-cta--ghost">
                  Explore products
                </a>
              </div>
            </FadeIn>
          </div>

          <FadeIn useScroll={false} delay={0.12} className="home-hero-visual">
            <ParallaxDrift>
            <div className="home-status-panel">
              <p className="home-status-title">Product status</p>
              <p className="home-status-subtitle">{statusLine}</p>

              <ul className="home-status-list">
                {products.map((product) => (
                  <li key={product.id} className="home-status-item">
                    <div>
                      <p className="home-status-name">{product.name}</p>
                      <p className="home-status-tagline">{product.tagline}</p>
                    </div>
                    {product.status === 'live' ? (
                      <a
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="home-status-link"
                      >
                        <span className="rw-live-dot rw-anim" aria-hidden="true" />
                        Live now
                      </a>
                    ) : (
                      <span className="home-status-badge">
                        <span className="rw-beta-dot" aria-hidden="true" />
                        {product.statusDetail ?? 'Coming soon'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              <div className="home-status-sample">
                <SampleCall />
              </div>
            </div>
            </ParallaxDrift>
          </FadeIn>
        </div>
      </section>

      <section className="landing-section home-proof-strip">
        <div className="landing-container">
          <StaggerContainer useScroll={true} className="home-proof-grid" stagger={0.08}>
            {homeProofStats.map((stat) => (
              <StaggerItem key={stat.id}>
                <article className="home-proof-item">
                  <p className="home-proof-value"><CountUp value={stat.value} /></p>
                  <p className="home-proof-label">{stat.label}</p>
                </article>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section className="landing-section" id="products-launchpad">
        <div className="landing-container">
          <FadeIn useScroll={true}>
            <p className="section-kicker">Product Launchpad</p>
            <h2 className="section-title">Pick the product for your real-world problem.</h2>
          </FadeIn>

          <StaggerContainer useScroll={true} className="products-grid" stagger={0.1}>
            {products.map((product) => (
              <StaggerItem key={product.id}>
                <ProductCard product={product} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section className="landing-section home-why" id="why-we-built">
        <div className="landing-container">
          <FadeIn useScroll={true} className="home-why-shell">
            <div>
              <p className="section-kicker">Why We Built Returned</p>
              <h2 className="section-title">Technology should make people feel more human, not less.</h2>
              <p className="home-why-copy">
                Returned came from hard lessons: after a massive startup loss, we stopped chasing hype and started building practical AI that removes friction from everyday life. The goal is not flashy demos. The goal is better human moments.
              </p>
              <Link to={PATHS.about} className="home-why-link">
                Read the founder story <ArrowRight size={16} />
              </Link>
            </div>

            <aside className="home-why-highlight">
              <div className="home-why-highlight-icon"><Zap size={18} /></div>
              <h3>The $6M Lesson</h3>
              <p>
                Great technology is not about sounding smart. It is about solving painful, real problems for real people.
              </p>
            </aside>
          </FadeIn>
        </div>
      </section>

      <section className="landing-section home-final-cta">
        <div className="landing-container">
          <SettleIn className="home-final-cta-shell">
            <p className="section-kicker">Move Now</p>
            <MaskRevealInView>
              <h2 className="section-title">Build more life into your day.</h2>
            </MaskRevealInView>
            <p className="home-final-cta-copy">Hand your phone calls to an AI that never gets tired of hold music, phone trees, or a ringing line.</p>
            <div className="rw-wave rw-anim" aria-hidden="true">
              {Array.from({ length: 36 }, (_, i) => <span key={i} />)}
            </div>
            <div className="home-final-cta-actions">
              <a href="https://helloreeva.com" target="_blank" rel="noopener noreferrer" className="home-hero-cta home-hero-cta--primary">
                Join the Reeva beta
              </a>
              <Link to={PATHS.blog} className="home-hero-cta home-hero-cta--ghost">
                Read our thinking
              </Link>
            </div>
          </SettleIn>
        </div>
      </section>
    </>
  )
}
